import { useId, useRef, useState } from "react";
import type { RefObject } from "react";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { ReasonField } from "@admin/_components/ReasonField";
import { reasonProblem } from "@admin/_helpers/reason";
import {
    RejectionReasonCategory,
    REJECTION_REASON_CATEGORY_LABEL,
    REJECTION_REASON_CATEGORY_OPTIONS,
} from "@models/location";

interface RejectLocationDialogProps {
    locationName: string;
    isProcessing: boolean;
    onConfirm: (
        category: RejectionReasonCategory,
        notes: string | undefined,
    ) => void;
    onClose: () => void;
    // Where focus lands on close when the trigger is gone — a successful
    // reject re-fetches the org and the row's Reject button unmounts.
    returnFocusRef?: RefObject<HTMLElement | null>;
}

export const RejectLocationDialog = ({
    locationName,
    isProcessing,
    onConfirm,
    onClose,
    returnFocusRef,
}: RejectLocationDialogProps) => {
    // Empty string = no category chosen yet; Reject stays disabled until one is.
    const [category, setCategory] = useState<RejectionReasonCategory | "">("");
    const [notes, setNotes] = useState("");
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    // Mid-request nothing dismisses and every control is locked: the request
    // already carries the chosen reason, and a failure keeps the dialog open
    // so the admin can retry with what they typed (LocationRow).
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
        returnFocusRef,
    });

    const categoryMissing = category === "";
    const notesTooLong = reasonProblem(notes, false) === "too-long";

    const handleConfirm = () => {
        if (categoryMissing) {
            return;
        }
        const trimmed = notes.trim();
        onConfirm(category, trimmed.length > 0 ? trimmed : undefined);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id={titleId} className="mb-2 text-lg font-bold">
                    Reject {locationName}?
                </h2>
                <p className="mb-4 text-sm">
                    This rejects the location and records the reason. It does not
                    change the organization.
                </p>

                <label className="mb-4 block">
                    <span className="mb-1 block text-sm font-semibold">
                        Reason
                    </span>
                    <select
                        className="select select-bordered w-full"
                        aria-label="Rejection reason"
                        value={category}
                        disabled={isProcessing}
                        onChange={(event) =>
                            setCategory(
                                event.target.value === ""
                                    ? ""
                                    : (Number(
                                        event.target.value,
                                    ) as RejectionReasonCategory),
                            )
                        }
                    >
                        <option value="">Select a reason…</option>
                        {REJECTION_REASON_CATEGORY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {REJECTION_REASON_CATEGORY_LABEL[option]}
                            </option>
                        ))}
                    </select>
                </label>

                <ReasonField
                    label="Notes (optional)"
                    value={notes}
                    onChange={setNotes}
                    disabled={isProcessing}
                    placeholder="Add any details for the applicant…"
                />

                <div className="flex justify-end gap-2">
                    <Button
                        color={ComponentColor.Error}
                        size={ComponentSize.SM}
                        loading={isProcessing}
                        disabled={categoryMissing || notesTooLong}
                        onClick={handleConfirm}
                    >
                        Reject
                    </Button>
                    <Button
                        color={ComponentColor.Neutral}
                        size={ComponentSize.SM}
                        disabled={isProcessing}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};
